/**
 * Vintage Christmas postcards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const vintageChristmasPostcardsPack: ToilePack = {
    slug: 'vintage-christmas-postcards',
    category: 'season',
    name: 'Vintage Christmas postcards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'robin', label: 'Robin', subject: 'a robin on a snowy holly branch with a lettered banner reading "Season\'s Greetings"'},
        {key: 'sleigh', label: 'Sleigh ride', subject: 'a horse-drawn sleigh in the snow with a lettered "Merry Christmas" banner'},
        {key: 'oval', label: 'Oval greeting', subject: 'an oval frame of holly with "A Merry Christmas" lettered inside'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a church instead', 'for a family photo card', 'in black and white'],
    directionHint: 'try: a church instead',
    seo: {
        title: 'Vintage Christmas Postcards: Robin, Sleigh Art',
        metaDescription: 'Nine vintage christmas postcards -- robin, sleigh ride, oval greeting -- in scandi folk, vintage card, and cozy cartoon finishes.',
        h1: 'Vintage Christmas postcard: robin, sleigh ride, and oval greeting',
        tagline: 'Nine cards, three classic scenes, three finishes each.',
        intro: 'Nine vintage-style christmas postcard designs: a robin on a snowy holly branch, a horse-drawn sleigh ride, and an oval holly frame lettered with a greeting. Three finishes -- scandi folk, vintage card, and cozy cartoon -- for printable cards, gift tags, and holiday mailings. Ask for a church instead, or a black-and-white version, and the same three finishes carry it.',
        useCases: [
            'a printable holiday postcard',
            'a gift tag design',
            'a holiday card mailing',
            'a scrapbook or journal print',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine postcard-style designs -- a robin, a sleigh ride, and an oval greeting -- each in scandi folk, vintage card, and cozy cartoon finishes, lettered with a classic holiday phrase.'},
            {q: 'What size does this print at?', a: 'The designs render at a wide postcard ratio; scale to a standard 4x6in or 5x7in card at your print shop.'},
            {q: 'Is this a real antique postcard?', a: 'No -- these are new illustrations designed to read as vintage, not scans of an actual historic card.'},
            {q: 'Can I get a different scene, like a church?', a: 'Yes -- the direction field takes any variation, including a church instead, a family-photo card crop, or a black-and-white finish.'},
        ],
    },
}
